/**
 * MysticBackground — atmospheric layer for the admin panel.
 *
 * Gives the "Gótico oscuro místico" vibe:
 *  - Base: deep obsidian gradient
 *  - Two huge, slow-drifting radial glows (antique gold + mystic violet)
 *  - A field of drifting "gold embers" (SVG points animated with CSS)
 *  - Everything is pointer-events-none and fixed behind the app
 *
 * Zero JS animation loop, zero deps beyond React + Tailwind.
 */
import React, { useMemo } from "react";

// Deterministic pseudo-random so hydration matches between renders.
function seeded(n) {
  const x = Math.sin(n * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

export default function MysticBackground({ intensity = 1 }) {
  const embers = useMemo(() => {
    const count = 22;
    return Array.from({ length: count }).map((_, i) => {
      const left = seeded(i + 1) * 100;                        // 0..100%
      const size = 1.4 + seeded(i + 11) * 2.6;                 // 1.4..4px
      const delay = -seeded(i + 21) * 6;                       // -6..0s (negative → start mid-animation)
      const duration = 5 + seeded(i + 31) * 7;                 // 5..12s
      const bottom = seeded(i + 41) * 100;                     // 0..100%
      const tone =
        seeded(i + 51) < 0.55 ? "rgba(212,175,55," : "rgba(139,92,246,";
      // Stable identity so React reconciles the ember DOM nodes correctly
      // across renders (memoized deps: [], so identity is set once).
      const emberId = `ember-${i}-${left.toFixed(2)}-${bottom.toFixed(2)}`;
      return { emberId, left, size, delay, duration, bottom, tone };
    });
  }, []);

  return (
    <div
      aria-hidden
      className="fixed inset-0 z-0 pointer-events-none overflow-hidden select-none"
      style={{ opacity: intensity }}
    >
      {/* Base obsidian gradient */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 50% -10%, #0a0714 0%, #050308 55%, #02030A 100%)",
        }}
      />

      {/* Cathedral vignette */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 55%, transparent 40%, rgba(0,0,0,0.55) 100%)",
        }}
      />

      {/* Antique gold glow (top-right) — slow drift */}
      <div
        className="absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full blur-[120px] animate-mystic-drift"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.28) 0%, rgba(212,175,55,0.05) 40%, transparent 70%)",
          animationDuration: "26s",
        }}
      />

      {/* Mystic violet glow (bottom-left) — counter drift */}
      <div
        className="absolute -bottom-40 -left-32 w-[48rem] h-[48rem] rounded-full blur-[130px] animate-mystic-drift"
        style={{
          background:
            "radial-gradient(circle, rgba(109,40,217,0.34) 0%, rgba(76,29,149,0.10) 45%, transparent 75%)",
          animationDelay: "-12s",
          animationDuration: "30s",
        }}
      />

      {/* Deep violet fog at center bottom */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[64rem] h-[16rem] rounded-full blur-[100px]"
        style={{
          background:
            "radial-gradient(ellipse, rgba(46,16,101,0.35) 0%, transparent 70%)",
        }}
      />

      {/* Ethereal embers — floating gold / violet motes */}
      <div className="absolute inset-0">
        {embers.map((e) => (
          <span
            key={e.emberId}
            className="absolute rounded-full animate-ember-rise"
            style={{
              left: `${e.left}%`,
              bottom: `${e.bottom}%`,
              width: `${e.size}px`,
              height: `${e.size}px`,
              background: `${e.tone}0.85)`,
              boxShadow: `0 0 ${e.size * 3}px ${e.tone}0.65)`,
              animationDelay: `${e.delay}s`,
              animationDuration: `${e.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Grain overlay for texture */}
      <div
        className="absolute inset-0 opacity-[0.045] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")",
        }}
      />
    </div>
  );
}
